import { ROLE_KEYS, ROLE_LABELS, type RoleKey } from '@ea/shared';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useDirectory } from '@/components/common';
import { Button, Dialog, Input } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { exitViewAs, startViewAs } from '@/lib/viewAs';

/** Top-bar button for the administrator and operations supervisor: see the platform as any role (or one person). */
export function ViewAsButton() {
  const { me } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const dir = useDirectory();
  const allowed = me?.roles.some((r) => r.key === 'system_administrator' || r.key === 'operations_supervisor');
  if (!me || !allowed || me.viewer) return null;
  const isAdmin = me.roles.some((r) => r.key === 'system_administrator');
  const match = (t: string) => !q || t.toLowerCase().includes(q.toLowerCase());
  const roleKeys = ROLE_KEYS.filter((k) => k !== 'system_administrator' && match(ROLE_LABELS[k]));
  const people = (dir.data ?? []).filter((p) => p.id !== me.id && (isAdmin || !p.roles.includes('system_administrator')) && match(p.fullName));
  const label = (roles: string[]) => roles.map((r) => ROLE_LABELS[r as RoleKey] ?? r).join(', ') || 'No role';
  // Matches the banner: administrators act as themselves while viewing; everyone else is read-only.
  const modeHint = isAdmin ? 'Changes you make are saved as you.' : 'Read-only: nothing can be changed while viewing.';
  const item = 'flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-surface-2';
  return (
    <>
      <Button size="sm" variant="ghost" icon={<Eye className="size-4" />} onClick={() => setOpen(true)} title={isAdmin ? 'See the platform exactly as a role sees it (changes you make are saved as you)' : 'See the platform exactly as a role sees it (read-only)'}>
        <span className="hidden md:inline">View as</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen} title="View as" description={`Open the platform as a role sees it — its dashboard, menu and screens, filled with company data. ${modeHint}`}>
        <div className="space-y-3">
          <Input placeholder="Search roles or people" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          <div className="max-h-[55vh] space-y-4 overflow-y-auto">
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Roles</h3>
              <ul className="divide-y divide-border rounded-lg border border-border">
                {roleKeys.map((k) => (
                  <li key={k}>
                    <button type="button" className={item} onClick={() => startViewAs(`role:${k}`)}>
                      {ROLE_LABELS[k]}
                      <Eye className="size-4 text-muted" />
                    </button>
                  </li>
                ))}
              </ul>
              {!roleKeys.length && <p className="text-sm text-muted">No role found.</p>}
            </section>
            {!!people.length && (
              <section>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">A specific person</h3>
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {people.map((p) => (
                    <li key={p.id}>
                      <button type="button" className={item} onClick={() => startViewAs(p.id)}>
                        <span>
                          {p.fullName}
                          <span className="ml-1 text-xs text-muted">· {label(p.roles)}</span>
                        </span>
                        <Eye className="size-4 text-muted" />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
}

/** Banner shown while "View as" is active (also in the representative portal). */
export function ViewAsBanner() {
  const { me } = useAuth();
  if (!me?.viewer) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-warning px-4 py-2 text-center text-sm font-medium text-white">
      <span>
        {me.viewer.asRole ? (
          <>
            {me.viewer.fullName}, you are viewing as the <b>{me.roles.map((r) => r.label).join(', ')}</b> role{me.viewer.canAct ? ' — changes you make are saved as you' : ' — read-only'}
          </>
        ) : (
          <>
            {me.viewer.fullName}, you are viewing as <b>{me.fullName}</b> ({me.roles.map((r) => r.label).join(', ')}){me.viewer.canAct ? ' — changes you make are saved as you' : ' — read-only'}
          </>
        )}
      </span>
      <button type="button" onClick={exitViewAs} className="inline-flex items-center gap-1 rounded-md bg-white/20 px-2 py-0.5 hover:bg-white/30">
        <EyeOff className="size-4" /> Exit
      </button>
    </div>
  );
}
